// The Clockers 64's screen contents, drawn pixel by pixel from the font bitmaps onto a small canvas.
// The CRT pass (crt.ts) turns this canvas into phosphor on curved glass.
//
// Canvas: 684 x 472 "screen pixels" = the CRT glass (855 x 590 source pixels) / 1.25.
// Two modes, as on a real home computer:
//   basic: the film's own BASIC prompt, large glyphs (3 x 4 screen pixels per font pixel) at the
//          positions the film shows them, so our screen can take over from the film unnoticed;
//   grid:  40 x 27 characters of 16 x 16 screen pixels (2 x 2 per font pixel) for the programs.

import GLYPHS from "./glyphs.json";

export const W = 684;
export const H = 472;
/** Source pixels per screen pixel. */
export const SCALE = 1.25;

const G = GLYPHS as Record<string, number[]>;

export const BASIC = {
  sx: 3,
  sy: 4,
  /** cell advance in screen pixels */
  cw: 24,
  /**
   * The film's lines: "CLOCKERS 64 BASIC V2" (indented), "READY.", then the cursor line. Glyph
   * origins; the font's empty left column (3 screen px here) puts the ink where the film has it
   * (measured: ink from source x 1605 and 1564, cap tops at y 679 and 743).
   */
  // (-4, -3) compensates the CRT pass's barrel curvature, which pulls the top left inwards
  lines: [
    { x: 77, y: 48 },
    { x: 44, y: 99 },
  ],
  /** first input line (where the film's cursor sits, source y 784) and the line pitch after it */
  inputX: 44,
  inputY: 132,
  pitch: 33,
  cursor: { dx: 3, w: 23, h: 30 },
};

export const GRID = { cols: 40, rows: 27, cw: 16, ch: 16, x0: 22, y0: 20, sx: 2, sy: 2 };

export const PHOSPHOR = "#ffb15e";

export interface Cell {
  ch: string;
  inv: boolean;
  dim: boolean;
}

export type Mode = "basic" | "grid";

export class Terminal {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  mode: Mode = "basic";
  /** basic mode: text per line, line 0-1 are the film's, 2.. are input/output lines */
  basicLines: string[] = ["CLOCKERS 64 BASIC V2", "READY.", ""];
  grid: Cell[][] = [];
  cursor = { on: true, col: 0, row: 2, blink: true };
  /** set when contents change; the CRT pass re-uploads the canvas */
  dirty = true;
  /** false for reduced motion: the cursor never blinks, whatever mode or program sets it */
  blinkAllowed = true;
  private lastBlink = -1;

  constructor() {
    this.canvas = document.createElement("canvas");
    this.canvas.width = W;
    this.canvas.height = H;
    this.ctx = this.canvas.getContext("2d", { alpha: true })!;
    this.clearGrid();
  }

  // ---------- basic mode ----------

  /** Append to the current basic line (typing). */
  type(ch: string): void {
    const i = this.basicLines.length - 1;
    this.basicLines[i] += ch;
    this.cursor.col = this.basicLines[i].length;
    this.dirty = true;
  }

  /** New basic line(s); `text` lines are printed, and the cursor goes to a fresh line after. */
  newline(...text: string[]): void {
    for (const t of text) this.basicLines.push(t);
    this.basicLines.push("");
    // scroll: keep the film's two lines out of it once the screen is full
    const maxInput = Math.floor((H - 16 - BASIC.inputY) / BASIC.pitch) + 1;
    while (this.basicLines.length - 2 > maxInput) this.basicLines.splice(2, 1);
    this.cursor.col = 0;
    this.dirty = true;
  }

  // ---------- grid mode ----------

  clearGrid(): void {
    this.grid = Array.from({ length: GRID.rows }, () =>
      Array.from({ length: GRID.cols }, () => ({ ch: " ", inv: false, dim: false })),
    );
    this.dirty = true;
  }

  /** Switch to the 40-column screen, cleared. */
  gridMode(): void {
    this.mode = "grid";
    this.clearGrid();
    this.cursor = { on: false, col: 0, row: 0, blink: true };
  }

  put(col: number, row: number, text: string, opts: { inv?: boolean; dim?: boolean } = {}): void {
    if (row < 0 || row >= GRID.rows) return;
    for (let i = 0; i < text.length; i++) {
      const c = col + i;
      if (c < 0 || c >= GRID.cols) continue;
      this.grid[row][c] = { ch: text[i], inv: !!opts.inv, dim: !!opts.dim };
    }
    this.dirty = true;
  }

  // ---------- drawing ----------

  /** Redraw if needed. `t` in ms drives the cursor blink (about 1.9 Hz, like the real thing). */
  render(t: number): boolean {
    const blinkOn = !(this.cursor.blink && this.blinkAllowed) || Math.floor(t / 266) % 2 === 0;
    const blinkKey = this.cursor.on ? (blinkOn ? 1 : 0) : 2;
    if (!this.dirty && blinkKey === this.lastBlink) return false;
    this.lastBlink = blinkKey;
    this.dirty = false;
    const c = this.ctx;
    c.clearRect(0, 0, W, H);
    c.fillStyle = PHOSPHOR;
    if (this.mode === "basic") {
      this.basicLines.forEach((text, i) => {
        const p = this.basicPos(i);
        this.text(p.x, p.y, text, BASIC.sx, BASIC.sy, BASIC.cw);
      });
      if (this.cursor.on && blinkOn) {
        const p = this.basicPos(this.basicLines.length - 1);
        c.fillRect(p.x + BASIC.cursor.dx + this.cursor.col * BASIC.cw, p.y, BASIC.cursor.w, BASIC.cursor.h);
      }
    } else {
      for (let r = 0; r < GRID.rows; r++) {
        for (let k = 0; k < GRID.cols; k++) {
          const cell = this.grid[r][k];
          const x = GRID.x0 + k * GRID.cw;
          const y = GRID.y0 + r * GRID.ch;
          c.globalAlpha = cell.dim ? 0.45 : 1;
          if (cell.inv) {
            // "inverse" as a lit bar behind full-brightness glyphs: true inverse (dark glyph in a
            // bright block) fills in under the CRT's bloom and becomes unreadable
            c.globalAlpha = 0.3;
            c.fillRect(x, y - 1, GRID.cw, GRID.ch);
            c.globalAlpha = 1;
            if (cell.ch !== " ") this.glyph(x, y, cell.ch, GRID.sx, GRID.sy);
          } else if (cell.ch !== " ") {
            this.glyph(x, y, cell.ch, GRID.sx, GRID.sy);
          }
        }
      }
      c.globalAlpha = 1;
      if (this.cursor.on && blinkOn) {
        c.fillRect(GRID.x0 + this.cursor.col * GRID.cw, GRID.y0 + this.cursor.row * GRID.ch, GRID.cw, GRID.ch);
      }
    }
    return true;
  }

  private basicPos(i: number): { x: number; y: number } {
    if (i < BASIC.lines.length) return BASIC.lines[i];
    return { x: BASIC.inputX, y: BASIC.inputY + (i - BASIC.lines.length) * BASIC.pitch };
  }

  private text(x: number, y: number, s: string, sx: number, sy: number, cw: number): void {
    for (let i = 0; i < s.length; i++) this.glyph(x + i * cw, y, s[i], sx, sy);
  }

  private glyph(x: number, y: number, ch: string, sx: number, sy: number): void {
    const rows = G[ch] ?? G[ch.toUpperCase()] ?? G["?"];
    const c = this.ctx;
    for (let r = 0; r < 8; r++) {
      const bits = rows[r];
      if (!bits) continue;
      let k = 0;
      while (k < 8) {
        if (bits & (0x80 >> k)) {
          const s = k;
          while (k < 8 && bits & (0x80 >> k)) k++;
          c.fillRect(x + s * sx, y + r * sy, (k - s) * sx, sy);
        } else k++;
      }
    }
  }
}
